import HamburgerFullSvg from 'components/_common/Icons/SVGIcons/HambergurFullSvg';
import HomeFooterIconSvg from 'components/_common/Icons/SVGIcons/HomeFooterIconSvg';
import LoginUserSvg from 'components/_common/Icons/SVGIcons/LoginUserSvg';
import SearchSvg from 'components/_common/Icons/SVGIcons/SearchSvg';
import { getStringPrefix } from 'helpers/CommonHelper';
import { getImageURL } from 'helpers/FileUploadHelper';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import { userProfileInfoSelector } from 'store/auth/selectors/authSelectors';
import { mobileMenuOpenSelector } from 'store/customer/selectors/CustomerGlobalSelectors';
import { setMobileMenuOpen } from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import LoginModal from '../Header/Modals/LoginModal';
import MinCartSection from '../Header/Sections/MinCartSection';
import ProfileDropdown from '../Header/Sections/ProfileDropdown';

import { OPEN_MOBILE_LOGIN_EVENT, type OpenMobileLoginDetail } from './openMobileLogin';

const MobileFooterBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userProfileInfo = useAppSelector(userProfileInfoSelector);
  // the burger drawer is rendered by the top header; this bar's Menu button opens the same one
  const isMenuOpen = useAppSelector(mobileMenuOpenSelector);

  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [stayOnPageAfterLogin, setStayOnPageAfterLogin] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    tab: 'buyer' | 'dealer';
    formType: 'login' | 'signup';
  }>({ tab: 'buyer', formType: 'login' });

  const openLoginModal = (
    tab: 'buyer' | 'dealer',
    formType: 'login' | 'signup',
    stayOnPage = false
  ) => {
    setModalConfig({ tab, formType });
    setStayOnPageAfterLogin(stayOnPage);
    setIsGuestModalOpen(true);
  };

  // other mobile surfaces (e.g. the filter drawer's Login) open this same popup
  useEffect(() => {
    const handleOpenLogin = (event: Event) =>
      openLoginModal(
        'buyer',
        'login',
        !!(event as CustomEvent<OpenMobileLoginDetail>).detail?.stayOnPage
      );
    window.addEventListener(OPEN_MOBILE_LOGIN_EVENT, handleOpenLogin);
    return () => window.removeEventListener(OPEN_MOBILE_LOGIN_EVENT, handleOpenLogin);
  }, []);

  const currentPath = location.pathname;

  const isHomeActive = currentPath === '/';
  const isBrowseActive = currentPath === '/products';
  const isCartActive = currentPath === '/cart';
  const isProfileActive = currentPath.startsWith('/profile');

  const handleProfileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (userProfileInfo) {
      setIsProfileOpen(true);
    } else {
      openLoginModal('buyer', 'login');
    }
  };

  return (
    <>
      <div className="mobile-footer-bar d-lg-none">
        <button
          className={`mobile-footer-bar__item ${isHomeActive ? 'is-active' : ''}`}
          onClick={() => navigate(customerMainRoutes.customerHome.path)}
        >
          <HomeFooterIconSvg width={24} height={24} />
          <span className="mobile-footer-bar__label">{t('home', { defaultValue: 'Home' })}</span>
        </button>

        <button
          className={`mobile-footer-bar__item ${isBrowseActive ? 'is-active' : ''}`}
          onClick={() => navigate(customerMainRoutes.productList.path)}
        >
          <SearchSvg width={24} height={24} />
          <span className="mobile-footer-bar__label">
            {t('browse', { defaultValue: 'Browse' })}
          </span>
        </button>

        <MinCartSection isMobileFooter={true} isActive={isCartActive} />

        <button
          className={`mobile-footer-bar__item ${isMenuOpen ? 'is-active' : ''}`}
          onClick={() => dispatch(setMobileMenuOpen(true))}
          aria-label={t('menu')}
        >
          <HamburgerFullSvg width={24} height={24} />
          <span className="mobile-footer-bar__label">{t('menu', { defaultValue: 'Menu' })}</span>
        </button>

        <button
          className={`mobile-footer-bar__item ${isProfileActive ? 'is-active' : ''}`}
          onClick={handleProfileClick}
        >
          {userProfileInfo ? (
            <span className="mobile-footer-bar__avatar">
              {userProfileInfo.profile_pic ? (
                <img
                  src={getImageURL(userProfileInfo.profile_pic)}
                  alt="User Profile"
                  className="mobile-footer-bar__avatar-img"
                />
              ) : (
                <span className="mobile-footer-bar__avatar-initials">
                  {getStringPrefix(`${userProfileInfo.first_name} ${userProfileInfo.last_name}`)}
                </span>
              )}
            </span>
          ) : (
            <LoginUserSvg width={24} height={24} />
          )}
          <span className="mobile-footer-bar__label">
            {userProfileInfo
              ? t('profile', { defaultValue: 'Profile' })
              : t('login', { defaultValue: 'Login' })}
          </span>
        </button>
      </div>

      {isProfileOpen && (
        <Offcanvas
          show={isProfileOpen}
          onHide={() => setIsProfileOpen(false)}
          placement="end"
          className="mobile-profile-offcanvas"
        >
          <Offcanvas.Header closeButton closeVariant="white">
            <Offcanvas.Title className="mobile-profile-offcanvas__title">
              {t('profile', { defaultValue: 'Profile' })}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="p-0">
            <ProfileDropdown isMobileDrawer={true} onClose={() => setIsProfileOpen(false)} />
          </Offcanvas.Body>
        </Offcanvas>
      )}

      {isGuestModalOpen && (
        <LoginModal
          onClose={() => setIsGuestModalOpen(false)}
          initialTab={modalConfig.tab}
          initialFormType={modalConfig.formType}
          stayOnPageAfterLogin={stayOnPageAfterLogin}
        />
      )}
    </>
  );
};

export default MobileFooterBar;
